import React, { FC } from 'react';
import { Box } from '@mui/material';
import { ISinglePro } from './state';
import IdentityRail from './IdentityRail';
import { KeyPointsCard, ExpertiseCard, KnownForCard, BiographyCard } from './ContentCards';
import { SectionCard } from './SectionCard';
import ExpertVideosSection, { ExpertVideosViewAll } from './ExpertVideosSection';
import { useUser } from '../../hooks/useUser';
import { isInternal, isBrand, isAM, isProTeam } from '../../utils';

interface Props {
  expert?: ISinglePro;
  isSharePage?: boolean;
  projectId?: string | null;
  projectScoped?: boolean;
}

/** Loaded layout: sticky identity rail left, evidence cards right. */
const SingleExpert: FC<Props> = ({ expert, isSharePage, projectId, projectScoped }) => {
  const { role } = useUser();
  const canEdit = !isSharePage && (isAM(role) || isProTeam(role));

  // Location + Known for are omitted on the public share page (fields aren't in
  // the public payload allowlist); grid spans reflow around missing cards.
  const knownForVisible =
    !isSharePage &&
    (!!expert?.best_known_for?.trim() ||
      !!expert?.bio_awards?.trim() ||
      !!expert?.bio_notable_projects?.trim() ||
      !!expert?.bio_favorite_achievements?.trim() ||
      canEdit);
  const expertiseVisible = (expert?.expertise?.length || 0) > 0;
  const showVideos = !isSharePage && (isBrand(role) || isInternal(role));

  return (
    <Box className="px-[24px] grid gap-5 md:grid-cols-[308px_minmax(0,1fr)] items-start">
      <IdentityRail expert={expert} isSharePage={isSharePage} />
      <Box className="grid gap-5 md:grid-cols-2 content-start min-w-0">
        <KeyPointsCard expert={expert} projectId={projectId} scoped={projectScoped} />
        <ExpertiseCard expert={expert} span2={!knownForVisible} />
        {knownForVisible && (
          <KnownForCard expert={expert} canEdit={canEdit} span2={!expertiseVisible} />
        )}
        <BiographyCard expert={expert} projectId={projectId} scoped={projectScoped} />
        {showVideos && (
          <SectionCard span2 label="Videos" right={<ExpertVideosViewAll />}>
            <ExpertVideosSection />
          </SectionCard>
        )}
      </Box>
    </Box>
  );
};

export default SingleExpert;
